import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  TypoCaption,
  TypoFootnote,
  TypoHeading,
  TypoHeading2,
} from '@/components/Typography.tsx';
import Loader from '@/components/Loader.tsx';
import ScoringGuideline from '@/components/ScoringGuideline.tsx';
import MissingTests from '@/components/MissingTests.tsx';
import { VerticalSeparator } from '@/components/Separator.tsx';
import { CollectionCard } from '@/components/CollectionsList.tsx';
import { ArrowLeft2 } from '@/components/Icons.tsx';
import useUser from '@/hooks/useUser.ts';
import DefaultThumbnail from '@/assets/video-thumbnail.png';
import STUDY_CENTER_PAGE from '@/graphql/queries/study-center-page.graphql';
import SAVE_VIDEO_SCORE from '@/graphql/mutations/save-video-score.graphql';
import SAVE_EXAM_SCORE from '@/graphql/mutations/save-exam-score.graphql';
import notify from '@/utils/notifications.tsx';

const CategoryQuestions = () => {
  const { pageId = '' } = useParams();
  const { user, loading } = useUser();
  const {
    items,
    scores,
    groupId,
    categoryName,
    expectedNumber,
    projectedPoints,
    loading: studyCenterLoading
  } = useStudyCenterData(Number(pageId));

  const fundamentalsScore =
    scores.tutorials.currentPoints + scores.bankTutorials.currentPoints + scores.packs.currentPoints;

  if (loading || studyCenterLoading) return <Loader />;
  if (!user.hasStudycenter) return <Navigate to="/dashboard" />;

  return (
    <>
      <div className="flex items-center justify-between px-10 pt-6 pb-4">
        <TypoHeading2 className="text-neutral-text-strong font-medium">
          {categoryName}
        </TypoHeading2>
        <ScoringGuideline guideType="VIDEO" />
      </div>

      <div className="bg-neutral3 rounded px-4 py-3 w-full max-w-[430px] mx-auto mb-6 sticky top-4 z-10">
        <div className="flex items-center justify-between">
          <TypoCaption className="text-neutral-text-soft flex-1">EXPECTED # OF  QUESTIONS</TypoCaption>
          <TypoCaption className="text-neutral-text-strong min-w-32">
            <strong>{expectedNumber} Questions</strong>
          </TypoCaption>
        </div>
        <div className="flex items-center justify-between my-1">
          <TypoCaption className="text-neutral-text-soft flex-1">PROJECTED POINT VALUE</TypoCaption>
          <TypoCaption className="text-neutral-text-strong min-w-32">
            <strong>
              {projectedPoints} points
            </strong>
          </TypoCaption>
        </div>
        <div className="flex items-center justify-between">
          <TypoCaption className="text-neutral-text-soft flex-1">YOUR FUNDAMENTALS SCORE</TypoCaption>
          <TypoCaption className="text-neutral-text-strong min-w-32">
            <strong>
              {fundamentalsScore} {fundamentalsScore === 1 ? 'point' : 'points'}
            </strong>
          </TypoCaption>
        </div>
      </div>

      <div className="w-full bg-neutral2 text-neutral2-text-soft rounded px-10 pt-4 pb-8">
        <Section
          backLink={`/study-center/group/${groupId}`}
          title={`${categoryName} Tutorials`}
          list={items.tutorials}
          possiblePoints={scores.tutorials.maxPoints}
          currentPoints={scores.tutorials.currentPoints}
        />
        <span className="block w-full h-px bg-shade-10 my-16" />

        <Section
          title="Question Bank Tutorials"
          list={items.bankTutorials}
          possiblePoints={scores.bankTutorials.maxPoints}
          currentPoints={scores.bankTutorials.currentPoints}
        />
        <span className="block w-full h-px bg-shade-10 my-16" />

        <Section
          title="SATPrac Packs"
          list={items.packs}
          possiblePoints={scores.packs.maxPoints}
          currentPoints={scores.packs.currentPoints}
          actionSlot={<ScoringGuideline guideType="READING" />}
        />
      </div>
    </>
  );
};

const Section = ({
  title = '',
  list = [],
  possiblePoints = 0,
  currentPoints = 0,
  backLink,
  actionSlot
}: {
  title: string;
  list: StudyCenterItemProps[];
  possiblePoints?: number;
  currentPoints?: number;
  backLink?: string;
  actionSlot?: ReactNode
}) => (
  <>
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-x-3 py-2 mt-2">
          {Boolean(backLink?.length) && (
            <Link
              to={backLink as string}
              className="flex flex-row items-center rounded px-4 py-2 gap-x-2 bg-shade-5 whitespace-nowrap"
            >
              <ArrowLeft2 width={16} height={16} /><span className="text-common-white text-callout">Back</span>
            </Link>
          )}
          <TypoHeading>{title}</TypoHeading>
        </div>
        <div className="flex items-center gap-3 mt-2 mb-4">
          <TypoFootnote>POSSIBLE POINTS: <strong>{possiblePoints}</strong></TypoFootnote>
          <VerticalSeparator className="w-1.5 !h-1.5 bg-neutral2-decoration" />
          <TypoFootnote>MY POINTS: <strong>{currentPoints}</strong></TypoFootnote>
        </div>
      </div>
      {actionSlot}
    </div>

    {list.length > 0 ? (
      <div className="grid grid-cols-3 flex-wrap gap-3">
        {list.map((item) => (
          <StudyCenterItem key={item.id} {...item} />
        ))}
      </div>
    ) : (
      <MissingTests text="No items yet." />
    )}
  </>
);

const StudyCenterItem = ({ id, name, collection, exam, video, videoId, score: defaultScore }: StudyCenterItemProps) => {
  const { pathname } = useLocation();
  const [score, setScore] = useState(defaultScore);
  const isPack = collection === CollectionType.Default;

  const [saveItem, { loading }] = useMutation(isPack ? SAVE_EXAM_SCORE : SAVE_VIDEO_SCORE, {
    refetchQueries: ['StudyCenterPage'],
    onCompleted: (data) => {
      setScore(data.scoreResponse.score);
      notify('Success!', 'Score saved.', { type: 'success' });
    },
    onError: (err) => console.error(err),
  });


  const onScoreUpdate = useCallback(async (value: number) =>
    saveItem({ variables: { id: Number(id), score: value } }), [id, saveItem]);

  return (
    <div className="flex flex-col gap-2 flex-1">
      {
        isPack
          ? (
            <Link
              to={`/practice-packs/${exam?.examGroup.examGroupId}/${exam?.examSubgroup.examSubgroupId}`}
              state={{ fromPath: pathname }}
            >
              <CollectionCard icon={exam?.examSubgroup.icon ?? 'PRACTICE'} name={name} />
            </Link>
          )
          : (
            <Link to={`/video-tutorials/video/${videoId}`}>
              <img
                src={video?.image || DefaultThumbnail}
                alt="Video Thumbnail"
                className="rounded-xl drop-shadow w-full aspect-video"
              />
            </Link>
          )
      }
      <div className="line-clamp-2 mx-2">
        <strong className="text-callout font-medium leading-4">
          {name}
        </strong>
      </div>

      <div className="bg-shade-5 rounded mt-auto py-1 text-center">
        <TypoFootnote className="block text-neutral2-text mb-2">{isPack ? 'Pack' : 'Video'} Score</TypoFootnote>
        <ScoreButtonGroup defaultScore={score} loading={loading} onUpdate={onScoreUpdate} />
      </div>

      <div className="flex justify-center items-center gap-1 pt-1 pb-3 px-2.5">
        <span className="bg-shade-10 w-2.5 h-px rounded-xs flex-shrink-0" />
        <span className="bg-shade-10 w-2.5 h-px rounded-xs flex-shrink-0" />
        <span className="bg-shade-10 w-2.5 h-px rounded-xs flex-shrink-0" />
      </div>
    </div>
  );
};

const ScoreButtonGroup = ({
  defaultScore,
  loading = false,
  onUpdate
}: { defaultScore?: number; loading?: boolean; onUpdate: (score: number) => void }) => (
  <div className="flex justify-center items-center gap-1">
    {Array.from({ length: MAX_SCORE }, (_, k) => k + 1).map((score) => (
      <button
        key={score}
        disabled={loading}
        onClick={() => onUpdate(score)}
        className={`
        flex items-center justify-center w-6 h-6 rounded-full ${defaultScore === score ?
        'text-warning-text bg-warning' : 'text-neutral2-text-strong bg-neutral2 hover:bg-neutral2-hover'}
        `}
      >
        <TypoCaption className="text-inherit font-semibold">{score}</TypoCaption>
      </button>
    ))}
  </div>
);

const useStudyCenterData = (id: number) => {
  const { data, loading } = useQuery(STUDY_CENTER_PAGE, {
    variables: { id },
    skip: !id,
  });

  const projectedPoints = data?.studyCenterPage?.projectedPoints ?? 0;

  const items = useMemo(() => {
    const videos = data?.studyCenterPage?.videos ?? [];
    const packs = data?.studyCenterPage?.exams ?? [];

    const bankTutorials = videos.filter((video: any) => video.collection === CollectionType.QuestionBank);
    const tutorials = videos.filter((video: any) => video.collection === CollectionType.QuestionType);

    return {
      tutorials,
      bankTutorials,
      packs
    };

  }, [data?.studyCenterPage?.exams, data?.studyCenterPage?.videos]);

  const scores = useMemo(() => {
    const all = data?.studyCenterPage?.collectionScores ?? [];
    const bankMaxPoints = Math.round(PERCENTAGE.QUESTION_BANK * projectedPoints);
    const tutorialsMaxPoints = Math.round(PERCENTAGE.TUTORIALS * projectedPoints);
    const packsMaxPoints = Math.round(PERCENTAGE.PACKS * projectedPoints);

    const bankTutorials = all.find((s: any) => s.collection === CollectionType.QuestionBank);
    const tutorials = all.find((s: any) => s.collection === CollectionType.QuestionType);
    const packs = all.find((s: any) => s.collection === CollectionType.Default);

    return {
      bankTutorials: {
        currentPoints:
          bankTutorials?.score > 0
            ? Math.round((bankTutorials.score / (MAX_SCORE * items.bankTutorials.length)) * bankMaxPoints) : 0,
        maxPoints: bankMaxPoints,
      },
      tutorials: {
        currentPoints:
          tutorials?.score > 0
            ? Math.round((tutorials.score / (MAX_SCORE * items.tutorials.length)) * tutorialsMaxPoints) : 0,
        maxPoints: tutorialsMaxPoints,
      },
      packs: {
        currentPoints:
          packs?.score > 0 ? Math.round((packs.score / (MAX_SCORE * items.packs.length)) * packsMaxPoints) : 0,
        maxPoints: packsMaxPoints,
      },
    };
  }, [data?.studyCenterPage?.collectionScores, items, projectedPoints]);

  return {
    items,
    scores,
    loading,
    projectedPoints,
    groupId: data?.studyCenterPage?.group?.studycenterGroupId ?? 0,
    categoryName: data?.studyCenterPage?.category?.name ?? '',
    expectedNumber: data?.studyCenterPage?.expectedNumber ?? '',
  };
};

interface StudyCenterItemProps {
  id: string;
  name: string;
  score: number;
  collection: CollectionType;
  exam?: {
    examGroup: {
      examGroupId: number;
    }
    examSubgroup: {
      examSubgroupId: number;
      icon: string;
    }
  };
  videoId: number;
  video?: {
    image?: string;
  };
}

const PERCENTAGE = {
  TUTORIALS: 0.45,
  QUESTION_BANK: 0.20,
  PACKS: 0.35
};

const MAX_SCORE = 5;

enum CollectionType {
  Default = 'default',
  QuestionBank = 'question_bank',
  QuestionType = 'question_type',
}

export default CategoryQuestions;
